import React from 'react';
import { graphql } from 'react-relay';
import { useFragment } from 'react-relay/hooks';
import classes from './DashboardOfferItem.module.scss';
import clsx from 'clsx';
import { DashboardOfferItem_offer$key } from './__generated__/DashboardOfferItem_offer.graphql';

import MobileIcon from '../../assets/smartphone.svg';
import AndroidIcon from '../../assets/android.svg';
import IosIcon from '../../assets/apple.svg';
import { CASHBACK, DEALS } from '../pages';
import Card from '../../atoms/Card/Card';
import A from 'components/atoms/A/A';
import Image from 'components/atoms/Image/Image';

export interface Props {
  offerRef: DashboardOfferItem_offer$key;
  className?: string;
}

const fragmentSpec = graphql`
  fragment DashboardOfferItem_offer on Offer {
    id
    title
    imageUrl
    guid
    advertiser {
      name
      imageUrl
    }
    tags
    paysReward
    rewardLabel
  }
`;

interface StyleProps {
  offerImage: string;
}

const DashboardOfferItem: React.FC<Props> = ({ offerRef, className }) => {
  const offer = useFragment(fragmentSpec, offerRef);
  const mobile: string | undefined = offer.tags.find((tag) =>
    ['ios_only', 'android_only', 'mobile_only'].includes(tag),
  );

  function mobileData(tag: string) {
    switch (tag) {
      case 'android_only':
        return {
          text: 'Android only',
          icon: <AndroidIcon className={classes.mobileOfferIcon} />,
          alt: 'An android phone logo',
        };
      case 'ios_only':
        return {
          text: 'iOS only',
          icon: <IosIcon className={classes.mobileOfferIcon} />,
          alt: 'An Apple iOS logo',
        };
      case 'mobile_only':
        return {
          text: 'Mobile only',
          icon: <MobileIcon className={classes.mobileOfferIcon} />,
          alt: 'An icon depicting a smartphone',
        };
      default:
        return {};
    }
  }

  function getOfferUrl() {
    const basePath = offer.paysReward ? CASHBACK.path : DEALS.path;
    return `${basePath}/${offer.guid}`;
  }

  function getTruncatedTitle() {
    if (offer.title.length > 50) {
      return `${offer.title.slice(0, 49).trim()}...`;
    }
    return offer.title;
  }

  const imageUrl = offer?.advertiser?.imageUrl || offer.imageUrl;
  return (
    <Card
      interactive
      component={A}
      className={clsx(classes.root, className, mobile && classes.mobileOfferLink)}
      to={getOfferUrl()}
    >
      {mobile && (
        <div className={classes.mobileOfferBanner}>
          {mobileData(mobile).icon}
          {mobileData(mobile).text}
        </div>
      )}
      <div
        className={clsx(
          classes.dashboardOfferItemContentWrapper,
          mobile && classes.mobileOfferOverlay,
        )}
      >
        <div className={classes.offerContent}>
          <div className={classes.topData}>
            {imageUrl && (
              <Image
                src={imageUrl}
                width={542}
                objectFit="contain"
                height={128}
                layout="responsive"
                alt={'Offer image'}
              />
            )}
          </div>
          <div className={classes.bottomData}>
            <div className={classes.bottomDataContent}>
              <p className={classes.advertiser}>{offer.advertiser?.name}</p>
              <p className={classes.truncatedTitle}>{getTruncatedTitle()}</p>
            </div>
            {offer.paysReward && (
              <div className={classes.reward}>
                <span>{offer.rewardLabel}</span>
              </div>
            )}
          </div>
          {/* <div className={classes.tags}>
            {offer.tags.map((tag) => (
              <Tag
                className={classes.tag}
                tag={tag}
                type={offer.paysReward ? 'cashback' : 'deal'}
                key={tag}
              />
            ))}
          </div> */}
        </div>
      </div>
    </Card>
  );
};

export default DashboardOfferItem;
